import { BS_EPOCH_AD, BS_FIRST_YEAR, BS_LAST_YEAR, BS_MONTH_LENGTHS } from './calendar-data.js';
import { BsInvalidDateError, BsRangeError } from './errors.js';
const MS_PER_DAY = 86_400_000;
/**
 * The IANA zone every conversion in this package uses. Nepal is UTC+05:45 today and
 * was UTC+05:30 before 1986-01-01; using the zone name rather than a hardcoded offset
 * is what makes historical instants correct too.
 */
export const NEPAL_TIME_ZONE = 'Asia/Kathmandu';
/** Day number (days since the Unix epoch) of a Gregorian civil date. */
function adToDayNumber(date) {
    return Date.UTC(date.year, date.month - 1, date.day) / MS_PER_DAY;
}
/** Gregorian civil date of a day number. */
function dayNumberToAd(dayNumber) {
    const date = new Date(dayNumber * MS_PER_DAY);
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}
/** Cumulative days from BS_FIRST_YEAR-01-01 to the first day of each covered year. */
const YEAR_START_OFFSETS = (() => {
    const offsets = [0];
    for (const row of BS_MONTH_LENGTHS) {
        const previous = offsets[offsets.length - 1];
        offsets.push(previous + row.reduce((sum, days) => sum + days, 0));
    }
    return offsets;
})();
const EPOCH_DAY_NUMBER = adToDayNumber(BS_EPOCH_AD);
function isWholeNumber(value) {
    return typeof value === 'number' && Number.isInteger(value);
}
/** Days in a BS month. Throws for a month outside 1..12 or a year outside coverage. */
export function daysInBsMonth(year, month) {
    if (!isWholeNumber(year) || !isWholeNumber(month)) {
        throw new BsInvalidDateError(`BS year and month must be whole numbers, received ${String(year)}-${String(month)}`);
    }
    if (month < 1 || month > 12) {
        throw new BsInvalidDateError(`BS month must be 1..12, received ${month}`);
    }
    assertBsYearSupported(year);
    const row = BS_MONTH_LENGTHS[year - BS_FIRST_YEAR];
    /* c8 ignore next — unreachable once the year is known supported; kept so a data-file edit fails loudly */
    if (!row)
        throw new BsRangeError(`No calendar data for BS ${year}`);
    return row[month - 1];
}
/** The first and last BS dates this package can convert. */
export const BS_SUPPORTED_RANGE = {
    first: { year: BS_FIRST_YEAR, month: 1, day: 1 },
    last: {
        year: BS_LAST_YEAR,
        month: 12,
        day: BS_MONTH_LENGTHS[BS_LAST_YEAR - BS_FIRST_YEAR][11],
    },
};
/** The same range expressed in Gregorian dates — the range a caller usually has in hand. */
export const AD_SUPPORTED_RANGE = {
    first: { ...BS_EPOCH_AD },
    last: dayNumberToAd(EPOCH_DAY_NUMBER + YEAR_START_OFFSETS[YEAR_START_OFFSETS.length - 1] - 1),
};
function rangeMessage() {
    const bs = `BS ${formatIso(BS_SUPPORTED_RANGE.first)} .. ${formatIso(BS_SUPPORTED_RANGE.last)}`;
    const ad = `AD ${formatIso(AD_SUPPORTED_RANGE.first)} .. ${formatIso(AD_SUPPORTED_RANGE.last)}`;
    return `${bs} (${ad}). Beyond this range the published Bikram Sambat calendar is not settled; extend the data file from an authoritative source rather than relaxing this bound (see packages/nepali-date/README.md).`;
}
function formatIso(date) {
    return `${String(date.year).padStart(4, '0')}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
}
export function isBsYearSupported(year) {
    return isWholeNumber(year) && year >= BS_FIRST_YEAR && year <= BS_LAST_YEAR;
}
function assertBsYearSupported(year) {
    if (!isBsYearSupported(year)) {
        throw new BsRangeError(`BS year ${year} is outside the supported range: ${rangeMessage()}`);
    }
}
/** True when the date exists in the Bikram Sambat calendar AND is inside our coverage. */
export function isValidBsDate(date) {
    try {
        assertValidBsDate(date);
        return true;
    }
    catch {
        return false;
    }
}
function assertValidBsDate(date) {
    if (!isWholeNumber(date.year) || !isWholeNumber(date.month) || !isWholeNumber(date.day)) {
        throw new BsInvalidDateError(`BS date components must be whole numbers, received ${JSON.stringify(date)}`);
    }
    if (date.month < 1 || date.month > 12) {
        throw new BsInvalidDateError(`BS month must be 1..12, received ${date.month}`);
    }
    if (date.day < 1) {
        throw new BsInvalidDateError(`BS day must be 1 or greater, received ${date.day}`);
    }
    assertBsYearSupported(date.year);
    const length = daysInBsMonth(date.year, date.month);
    if (date.day > length) {
        throw new BsInvalidDateError(`BS ${formatIso(date)} does not exist: month ${date.month} of year ${date.year} has ${length} days`);
    }
}
function assertValidAdDate(date) {
    if (!isWholeNumber(date.year) || !isWholeNumber(date.month) || !isWholeNumber(date.day)) {
        throw new BsInvalidDateError(`AD date components must be whole numbers, received ${JSON.stringify(date)}`);
    }
    const roundTrip = dayNumberToAd(adToDayNumber(date));
    if (roundTrip.year !== date.year || roundTrip.month !== date.month || roundTrip.day !== date.day) {
        throw new BsInvalidDateError(`AD ${formatIso(date)} is not a real Gregorian date`);
    }
}
/** Bikram Sambat -> Gregorian. Throws outside the supported range; never guesses. */
export function bsToAd(date) {
    assertValidBsDate(date);
    let offset = YEAR_START_OFFSETS[date.year - BS_FIRST_YEAR];
    for (let month = 1; month < date.month; month += 1) {
        offset += daysInBsMonth(date.year, month);
    }
    offset += date.day - 1;
    return dayNumberToAd(EPOCH_DAY_NUMBER + offset);
}
/** Gregorian -> Bikram Sambat. Throws outside the supported range; never guesses. */
export function adToBs(date) {
    assertValidAdDate(date);
    const offset = adToDayNumber(date) - EPOCH_DAY_NUMBER;
    const totalDays = YEAR_START_OFFSETS[YEAR_START_OFFSETS.length - 1];
    if (offset < 0 || offset >= totalDays) {
        throw new BsRangeError(`AD ${formatIso(date)} is outside the supported range: ${rangeMessage()}`);
    }
    let low = 0;
    let high = BS_MONTH_LENGTHS.length - 1;
    while (low < high) {
        const mid = Math.floor((low + high + 1) / 2);
        if (YEAR_START_OFFSETS[mid] <= offset)
            low = mid;
        else
            high = mid - 1;
    }
    const year = BS_FIRST_YEAR + low;
    let remaining = offset - YEAR_START_OFFSETS[low];
    for (let month = 1; month <= 12; month += 1) {
        const length = daysInBsMonth(year, month);
        if (remaining < length)
            return { year, month, day: remaining + 1 };
        remaining -= length;
    }
    /* c8 ignore next 2 — unreachable: the year offsets are the sum of its month lengths */
    throw new BsRangeError(`AD ${formatIso(date)} could not be placed in BS ${year}`);
}
let kathmanduFormatter;
function getKathmanduFormatter(timeZone) {
    if (timeZone === NEPAL_TIME_ZONE && kathmanduFormatter)
        return kathmanduFormatter;
    const formatter = new Intl.DateTimeFormat('en-US-u-ca-gregory', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    });
    assertZoneBehavesCorrectly(formatter, timeZone);
    if (timeZone === NEPAL_TIME_ZONE)
        kathmanduFormatter = formatter;
    return formatter;
}
/**
 * A Node build without full ICU silently resolves every zone to UTC. That would shift
 * the BS date of anything registered between 18:15 and 24:00 UTC by a day, and around
 * Chaitra's end it would shift the BS YEAR inside a tracking code.
 *
 * We check BEHAVIOUR, not the resolved zone's spelling: ICU canonicalises
 * "Asia/Kathmandu" to the legacy alias "Asia/Katmandu", so a name comparison would
 * reject a perfectly correct runtime. The offset is what matters.
 */
function assertZoneBehavesCorrectly(formatter, timeZone) {
    const resolved = formatter.resolvedOptions().timeZone;
    if (timeZone !== NEPAL_TIME_ZONE) {
        if (resolved.toLowerCase() !== timeZone.toLowerCase() && resolved === 'UTC') {
            throw new BsInvalidDateError(`Runtime cannot resolve time zone "${timeZone}" (fell back to "${resolved}"). ` +
                'Node must be built with full ICU; refusing to convert against the wrong clock.');
        }
        return;
    }
    // 2020-01-01T00:00:00Z is 05:45 on 2020-01-01 in Nepal (+05:45).
    const probe = formatter.formatToParts(new Date('2020-01-01T00:00:00.000Z'));
    const read = (type) => probe.find((part) => part.type === type)?.value ?? '';
    const observed = `${read('year')}-${read('month')}-${read('day')} ${read('hour')}:${read('minute')}`;
    if (observed !== '2020-01-01 05:45') {
        throw new BsInvalidDateError(`Runtime does not implement "${timeZone}" correctly (resolved to "${resolved}"; ` +
            `2020-01-01T00:00:00Z rendered as "${observed}", expected "2020-01-01 05:45"). ` +
            'Node must be built with full ICU; refusing to convert against the wrong clock.');
    }
}
/** The Gregorian civil date an instant falls on, on the Nepal wall clock. */
export function instantToAdCivil(instant, timeZone = NEPAL_TIME_ZONE) {
    if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
        throw new BsInvalidDateError('instant must be a valid Date');
    }
    const parts = getKathmanduFormatter(timeZone).formatToParts(instant);
    const read = (type) => {
        const part = parts.find((candidate) => candidate.type === type);
        /* c8 ignore next — Intl always emits the parts we requested */
        if (!part)
            throw new BsInvalidDateError(`Intl did not return a "${type}" part`);
        return Number(part.value);
    };
    return { year: read('year'), month: read('month'), day: read('day') };
}
/**
 * The Bikram Sambat date an instant falls on, on the Nepal wall clock.
 * This is the one function the write path uses — a file registered at 00:15 NPT on
 * Baisakh 1 belongs to the NEW BS year, and its tracking code must say so.
 */
export function instantToBs(instant, timeZone = NEPAL_TIME_ZONE) {
    return adToBs(instantToAdCivil(instant, timeZone));
}
//# sourceMappingURL=calendar.js.map